import { alpha, styled, Typography } from '@mui/material';
import { TransactionType } from './Types';

const TableActionContainer = styled('div')(({ theme }) => ({
  padding: '32px 16px',
  display: 'grid',
  gridTemplateColumns: '400px 1fr 1fr 1fr 1fr 140px ',
  columnGap: '15px',
  overflowX: 'auto',
  whiteSpace: 'nowrap',

  [theme.breakpoints.down('xl')]: {
    padding: '32px 0px',
    columnGap: 10,
    gridTemplateColumns: '380px 0.5fr 1.5fr 1fr 1fr 1fr',
  },
  [theme.breakpoints.down('lg')]: {
    padding: '32px 0px',
    gap: 15,
    gridTemplateColumns: '340px 1fr 1.5fr',
    gridTemplateRows: 'repeat(2,1fr)',
    '.whiteSpace': {
      display: 'none',
    },
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    marginBottom: '60px',
    marginTop: '0px',
  },
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    div: {
      width: 'auto',
    },
  },
}));

const CreditWalletTableActionContainer = styled('div')(({ theme }) => ({
  padding: '32px 0',
  display: 'grid',
  gridTemplateColumns: '400px 1fr 1fr 1fr 1fr 140px',
  columnGap: '15px',
  [theme.breakpoints.down('xl')]: {
    padding: '32px 0px',
    columnGap: 10,
    gridTemplateColumns: '380px 0.5fr 1.5fr 1fr 1fr 1fr',
  },
  [theme.breakpoints.down('lg')]: {
    padding: '32px 0px',
    gap: 15,
    gridTemplateColumns: '340px 1fr 1.5fr',
    gridTemplateRows: 'repeat(2,1fr)',
    '.whiteSpace': {
      display: 'none',
    },
  },
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
    div: {
      width: 'auto',
    },
  },
}));

const NavigationBtnContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  gap: '20px',
  alignItems: 'stretch',

  [theme.breakpoints.down('xl')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },

  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: '16px',
  },
}));

const NavigationBtn = styled('div')<{
  isOverLimit?: boolean;
  isHighlighted?: boolean;
}>(({ theme, isOverLimit, isHighlighted }) => ({
  position: 'relative',
  overflow: 'hidden',

  padding: '24px',
  display: 'grid',
  gridTemplateColumns: '48px 1fr',
  alignItems: 'center',
  gap: '16px',

  border: '1px solid',
  borderRadius: '18px',

  borderColor: isOverLimit
    ? theme.palette.error.main
    : isHighlighted
    ? alpha(theme.palette.primary.main, 0.3)
    : alpha(theme.palette.divider, 0.8),

  background: isOverLimit
    ? `linear-gradient(
        135deg,
        ${alpha(theme.palette.error.main, 0.08)},
        ${alpha(theme.palette.error.main, 0.02)}
      )`
    : isHighlighted
    ? `linear-gradient(
        135deg,
        ${alpha(theme.palette.primary.main, 0.14)},
        ${alpha(theme.palette.primary.main, 0.04)}
      )`
    : alpha(theme.palette.background.paper, 0.7),

  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',

  color: isOverLimit ? theme.palette.error.main : 'inherit',

  height: '100%',

  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',

  boxShadow: isHighlighted ? `0 10px 30px ${alpha(theme.palette.primary.main, 0.12)}` : `0 6px 18px rgba(0,0,0,0.05)`,

  // 🔥 premium top shine effect
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-120%',
    width: '80%',
    height: '100%',
    background: `linear-gradient(
      90deg,
      transparent,
      ${alpha(theme.palette.common.white, 0.22)},
      transparent
    )`,
    transform: 'skewX(-25deg)',
    transition: '0.8s',
  },

  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: isHighlighted
      ? `0 16px 40px ${alpha(theme.palette.primary.main, 0.22)}`
      : `0 14px 34px rgba(0,0,0,0.10)`,

    '&::after': {
      left: '140%',
    },
  },

  // 🔥 left active glow bar
  ...(isHighlighted && {
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '12%',
      bottom: '12%',
      width: '4px',
      borderRadius: '0 10px 10px 0',
      background: `linear-gradient(
        180deg,
        ${theme.palette.primary.light},
        ${theme.palette.primary.main},
        ${theme.palette.primary.dark}
      )`,
      boxShadow: `0 0 14px ${alpha(theme.palette.primary.main, 0.5)}`,
    },
  }),

  [theme.breakpoints.down('xl')]: {
    gridTemplateColumns: '40px 1fr',
    padding: '20px',
  },

  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '36px 1fr',
    padding: '16px',
    gap: '12px',
    borderRadius: '16px',
  },
}));

export const IconWrapper = styled('div')<{ isOverLimit?: boolean }>(({ theme, isOverLimit }) => ({
  position: 'relative',

  width: '48px',
  height: '48px',
  borderRadius: '14px',

  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  background: isOverLimit
    ? `linear-gradient(
          135deg,
          ${alpha(theme.palette.error.main, 0.18)},
          ${alpha(theme.palette.error.main, 0.08)}
        )`
    : `linear-gradient(
          135deg,
          ${theme.palette.primary.main},
          ${theme.palette.primary.dark}
        )`,

  color: isOverLimit ? theme.palette.error.main : theme.palette.common.white,

  boxShadow: isOverLimit
    ? `0 8px 20px ${alpha(theme.palette.error.main, 0.18)}`
    : `0 10px 24px ${alpha(theme.palette.primary.main, 0.28)}`,

  transition: 'all 0.3s ease',

  '& svg': {
    fontSize: '24px',
    zIndex: 2,
  },

  '&:hover': {
    transform: 'scale(1.06) rotate(-2deg)',
  },

  [theme.breakpoints.down('xl')]: {
    width: '40px',
    height: '40px',
  },

  [theme.breakpoints.down('sm')]: {
    width: '36px',
    height: '36px',
  },
}));

export const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 700,
  lineHeight: 1.2,
  marginTop: '6px',

  fontFeatureSettings: '"tnum" on, "lnum" on',
  letterSpacing: '-0.03em',

  color: theme.palette.text.primary,

  transition: 'all 0.25s ease',

  [theme.breakpoints.down('xl')]: {
    fontSize: '21px',
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: '18px',
  },
}));

export const StatLabel = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '6px',

  fontSize: '14px',
  color: theme.palette.text.secondary,
  fontWeight: 500,

  letterSpacing: '0.02em',

  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
  },
}));

export const CurrencyBadge = styled('span')(({ theme }) => ({
  background: alpha(theme.palette.primary.main, 0.08),

  color: theme.palette.primary.main,

  fontSize: '11px',
  fontWeight: 700,

  padding: '3px 8px',

  borderRadius: '999px',

  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,

  backdropFilter: 'blur(8px)',
}));

const FilterSectionContainer = styled('div')`
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr;
  grid-gap: 24px;
  margin-bottom: 30px;
`;

const FilterSection = styled('div')<{ hasTwoDivisions?: boolean }>(({ theme, hasTwoDivisions }) => ({
  height: '130px',
  padding: '20px',
  display: 'grid',
  gridTemplateColumns: hasTwoDivisions ? '1fr 1fr' : '1fr 1fr 2fr',
  borderRadius: '8px',
  border: `1px solid ${theme.palette.border.grey}`,
  backgroundColor: theme.palette.primary.light,
}));

const FilterLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.purple,
  fontSize: '20px',
  fontWeight: 500,
}));

const DebitedIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.error.light,
  color: theme.palette.error.dark,
  width: 28,
  height: 28,
  borderRadius: 20,
  marginRight: 10,
}));

const CreditedIcon = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.palette.success.light,
  color: theme.palette.success.dark,
  width: 28,
  height: 28,
  borderRadius: 20,
  marginRight: 10,
}));

const IconStyle = { width: '33px', height: '33px' };

const TransactionStatus = styled(Typography)(() => ({
  // backgroundColor: theme.palette.background.grey,
  paddingBlock: 5,
  width: 120,
  marginInline: 'auto',
  textAlign: 'center',
  fontWeight: 500,
  borderRadius: 2,
}));

const AmountTypo = styled(Typography)<{ type: TransactionType }>(({ theme }) => ({
  color: theme.palette.common.black,
  fontWeight: 600,
}));

export {
  TableActionContainer,
  CreditWalletTableActionContainer,
  NavigationBtnContainer,
  NavigationBtn,
  FilterSectionContainer,
  FilterSection,
  FilterLabel,
  IconStyle,
  DebitedIcon,
  CreditedIcon,
  TransactionStatus,
  AmountTypo,
};
